/**
 * URL Detection utilities for chat input
 */

// Enhanced URL regex để detect nhiều loại URLs - support multiline và spaces
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm;

/**
 * Detect URLs in text
 * @param {string} text - Input text
 * @returns {Array} Array of detected URLs with positions
 */
export const detectURLs = (text) => {
    const urls = [];
    let match;
    
    // Reset regex lastIndex
    URL_REGEX.lastIndex = 0;
    
    while ((match = URL_REGEX.exec(text)) !== null) {
        urls.push({
            url: match[0],
            start: match.index,
            end: match.index + match[0].length,
            type: getURLType(match[0])
        });
    }
    
    return urls;
};

/**
 * Determine URL type (website or file)
 * @param {string} url - URL to analyze
 * @returns {string} 'file' or 'website'
 */
export const getURLType = (url) => {
    const fileExtensions = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
        'txt', 'rtf', 'csv', 'xml', 'json',
        'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg',
        'mp4', 'avi', 'mov', 'wmv', 'flv',
        'mp3', 'wav', 'flac', 'aac',
        'zip', 'rar', '7z', 'tar', 'gz'
    ];
    
    const urlLower = url.toLowerCase();
    const hasFileExtension = fileExtensions.some(ext => 
        urlLower.includes(`.${ext}`) || urlLower.endsWith(`.${ext}`)
    );
    
    return hasFileExtension ? 'file' : 'website';
};

/**
 * Format URL for display (truncate if too long)
 * @param {string} url - URL to format
 * @param {number} maxLength - Maximum display length
 * @returns {string} Formatted URL
 */
export const formatURLForDisplay = (url, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    
    const start = url.substring(0, Math.floor(maxLength * 0.6));
    const end = url.substring(url.length - Math.floor(maxLength * 0.3));
    
    return `${start}...${end}`;
};

/**
 * Parse text and return segments with URLs marked
 * Supports multiline text, spaces, và various separators
 * @param {string} text - Input text
 * @returns {Array} Array of text segments with type info
 */
export const parseTextWithURLs = (text) => {
    if (!text || typeof text !== 'string') {
        return [{ type: 'text', content: text || '' }];
    }
    
    const urls = detectURLs(text);
    if (urls.length === 0) {
        return [{ type: 'text', content: text }];
    }
    
    const segments = [];
    let lastIndex = 0;
    
    // Sort URLs by position để process theo thứ tự
    urls.sort((a, b) => a.start - b.start);
    
    urls.forEach(urlInfo => {
        // Add text before URL (including whitespace and newlines)
        if (urlInfo.start > lastIndex) {
            const textContent = text.substring(lastIndex, urlInfo.start);
            if (textContent) { // Keep whitespace và newlines
                segments.push({ type: 'text', content: textContent });
            }
        }
        
        // Add URL
        segments.push({
            type: 'url',
            content: urlInfo.url,
            urlType: urlInfo.type,
            start: urlInfo.start,
            end: urlInfo.end
        });
        
        lastIndex = urlInfo.end;
    });
    
    // Add remaining text (including trailing whitespace)
    if (lastIndex < text.length) {
        const textContent = text.substring(lastIndex);
        if (textContent) {
            segments.push({ type: 'text', content: textContent });
        }
    }
    
    return segments;
};

/**
 * Extract all URLs from text
 * @param {string} text - Input text
 * @returns {Array} Array of URL strings
 */
export const extractURLs = (text) => {
    const urls = detectURLs(text);
    return urls.map(urlInfo => urlInfo.url);
};

/**
 * Extract unique URLs from text with normalization and deduplication
 * @param {string} text - Input text
 * @returns {Array} Array of unique, normalized URL strings
 */
export const extractUniqueURLs = (text) => {
    const urls = extractURLs(text);
    return removeDuplicateURLs(urls);
};

/**
 * Remove duplicate URLs from array with normalization
 * @param {Array} urls - Array of URL strings
 * @returns {Array} Array of unique URLs
 */
export const removeDuplicateURLs = (urls) => {
    if (!Array.isArray(urls)) return [];
    
    const seen = new Set();
    const uniqueUrls = [];
    
    urls.forEach(url => {
        const normalizedUrl = normalizeURL(url);
        if (normalizedUrl && !seen.has(normalizedUrl)) {
            seen.add(normalizedUrl);
            uniqueUrls.push(url); // Keep original URL for display
        }
    });
    
    return uniqueUrls;
};

/**
 * Normalize URL for comparison (remove trailing slash, convert to lowercase domain)
 * @param {string} url - URL to normalize
 * @returns {string} Normalized URL
 */
export const normalizeURL = (url) => {
    try {
        const urlObj = new URL(url);
        
        // Normalize domain to lowercase
        urlObj.hostname = urlObj.hostname.toLowerCase();
        
        // Remove trailing slash from pathname if it's just "/"
        if (urlObj.pathname === '/') {
            urlObj.pathname = '';
        }
        
        // Sort search params for consistent comparison
        urlObj.searchParams.sort();
        
        return urlObj.toString();
    } catch {
        // If URL is invalid, return lowercase version for basic comparison
        return url.toLowerCase().trim();
    }
};

/**
 * Validate if string is a valid URL
 * @param {string} str - String to validate
 * @returns {boolean} True if valid URL
 */
export const isValidURL = (str) => {
    try {
        new URL(str);
        return true;
    } catch {
        return false;
    }
};

/**
 * Get domain from URL for display
 * @param {string} url - URL to extract domain from
 * @returns {string} Domain name
 */
export const getDomainFromURL = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch {
        return url;
    }
};
